import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';

export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;       // Absolute URL to OG image
  ogImage?: string;     // Backwards-compatibility wrapper
  url?: string;         // Canonical URL override (auto-detected if omitted)
  type?: string;        // OG type: 'website' | 'product' | 'article'
  noIndex?: boolean;    // Set true for private/auth pages
  jsonLd?: object;      // Structured data (JSON-LD)
}

const SITE_NAME = 'Kawawa Motors';
const BASE_URL  = 'https://www.kawawamotors.com';

/** All hostnames that are considered "real" production — everything else is staging. */
const PRODUCTION_HOSTS = ['kawawamotors.com', 'www.kawawamotors.com'];

const DEFAULT_IMAGE = `${BASE_URL}/assets/images/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  'Kawawa Motors – Malawi\'s leading marketplace for vehicles, spare parts, and repair services. ' +
  'Find the best deals on cars, trucks, motorcycles, and more.';
const DEFAULT_KEYWORDS =
  'Kawawa Motors, buy car Malawi, vehicle marketplace, used cars Malawi, repair garage Malawi, spare parts Malawi';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  /** True only on kawawamotors.com — false on staging, localhost, etc. */
  private readonly isProductionDomain: boolean;

  constructor(
    private title: Title,
    private meta: Meta,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // Detect which domain this build is running on RIGHT NOW in the browser.
    const currentHost = this.document.location?.hostname ?? '';
    this.isProductionDomain = PRODUCTION_HOSTS.includes(currentHost);

    // If we're on a staging/local domain, immediately stamp noindex globally
    // so even the very first page load is not crawlable.
    if (!this.isProductionDomain) {
      this.forceNoIndex();
    }
  }

  /**
   * Primary method — call from each component's ngOnInit
   * with page-specific SEO config.
   */
  updateSeo(config: SeoConfig = {}): void {
    const pageTitle = config.title
      ? `${config.title} | ${SITE_NAME}`
      : `${SITE_NAME} | Buy, Sell & Rent Vehicles in Malawi`;

    const description = config.description || DEFAULT_DESCRIPTION;
    const keywords    = config.keywords    || DEFAULT_KEYWORDS;
    const image       = config.image || config.ogImage || DEFAULT_IMAGE;
    const imageAlt    = `${pageTitle} preview`;

    // Canonical always points to kawawamotors.com regardless of which domain
    // the user is currently browsing on (staging, localhost, etc.)
    const url         = config.url || `${BASE_URL}${this.router.url.split('?')[0]}`;
    const type        = config.type || 'website';

    // Staging override: NEVER index non-production domains
    const shouldNoIndex = !this.isProductionDomain || !!config.noIndex;

    // ── 1. Document Title ──────────────────────────────────────────────────
    this.title.setTitle(pageTitle);

    // ── 2. Standard Meta Tags ─────────────────────────────────────────────
    this.setTag('description',  description);
    this.setTag('keywords',     keywords);
    this.setTag('robots', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');

    // ── 3. Open Graph (Facebook / WhatsApp / LinkedIn) ────────────────────
    this.setProp('og:title',       pageTitle);
    this.setProp('og:description', description);
    this.setProp('og:image',       image);
    this.setProp('og:image:alt',   imageAlt);
    this.setProp('og:url',         url);
    this.setProp('og:type',        type);
    this.setProp('og:site_name',   SITE_NAME);
    this.setProp('og:locale',      'en_MW');

    // ── 4. Twitter / X Cards ──────────────────────────────────────────────
    this.setTag('twitter:card',        'summary_large_image');
    this.setTag('twitter:title',       pageTitle);
    this.setTag('twitter:description', description);
    this.setTag('twitter:image',       image);
    this.setTag('twitter:image:alt',   imageAlt);

    // ── 5. Canonical URL ──────────────────────────────────────────────────
    this.setCanonical(url);

    // ── 6. JSON-LD Structured Data ────────────────────────────────────────
    if (config.jsonLd) {
      this.setJsonLd(config.jsonLd);
    } else {
      this.removeJsonLd();
    }
  }

  /**
   * Reset to site defaults (call on generic / auth pages).
   */
  resetToDefaults(): void {
    this.updateSeo();
  }

  /**
   * Inject or update JSON-LD structured schema in head.
   */
  updateSchema(schemaObj: any): void {
    if (schemaObj) {
      this.setJsonLd(schemaObj);
    } else {
      this.removeJsonLd();
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private setTag(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }

  private setProp(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private setCanonical(url: string): void {
    const existing = this.document.head.querySelector('link[rel="canonical"]');
    if (existing) {
      existing.setAttribute('href', url);
    } else {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      this.document.head.appendChild(link);
    }
  }

  private setJsonLd(data: object): void {
    this.removeJsonLd();
    const script: HTMLScriptElement = this.document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', 'seo-json-ld');
    script.textContent = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  private removeJsonLd(): void {
    const existing = this.document.head.querySelector('#seo-json-ld');
    if (existing) existing.remove();
  }

  /**
   * Called once in the constructor when NOT on production domain.
   * Stamps noindex globally so the page loads on staging are blocked.
   */
  private forceNoIndex(): void {
    this.setTag('robots', 'noindex, nofollow');
    const path = this.document.location?.pathname ?? '/';
    this.setCanonical(`${BASE_URL}${path}`);
  }
}
